/**
 * AnnexFormDialog — Create / Edit Annex (phụ lục hợp đồng).
 * Theo SOP Table 56:
 *   - contract_no (autocomplete LocalP + NPE union — useListAllContractsQuery)
 *   - supplier display
 *   - amendment_no (auto-gen — display "Auto" khi tạo)
 *   - amendment_date (default today)
 *   - content_before_change (multi-line)
 *   - content_after_change (multi-line)
 *   - price (auto từ contract.outright_price, optional override)
 *   - quantity (auto từ contract.quantity_kg, optional override)
 *   - note
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateAnnexMutation,
  useUpdateAnnexMutation,
} from '@/store/api/annexesApi'
import { useListAllContractsQuery } from '@/store/api/contractsAllApi'
import { useGetLocalPQuery } from '@/store/api/localPApi'
import type { AnnexCreateIn, AnnexOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

export interface AnnexFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  /** Khi mở từ contract detail thì lock contract_no. */
  fixedContractNo?: string
  initialValue?: AnnexOut | null
  onClose: () => void
  onSaved?: (id: number) => void
}

interface AnnexFormValues {
  contract_no: string
  annex_date: string
  content_before_change: string
  content_after_change: string
  price: number | ''
  price_override: boolean
  quantity: number | ''
  quantity_override: boolean
  note: string
}

const EMPTY: AnnexFormValues = {
  contract_no: '',
  annex_date: dayjs().format('YYYY-MM-DD'),
  content_before_change: '',
  content_after_change: '',
  price: '',
  price_override: false,
  quantity: '',
  quantity_override: false,
  note: '',
}

const schema: yup.ObjectSchema<AnnexFormValues> = yup.object({
  contract_no: yup.string().trim().required('Bắt buộc chọn hợp đồng'),
  annex_date: yup.string().required('Bắt buộc chọn ngày'),
  content_before_change: yup.string().trim().default(''),
  content_after_change: yup.string().trim().default(''),
  price: yup.mixed<number | ''>().default(''),
  price_override: yup.boolean().default(false),
  quantity: yup.mixed<number | ''>().default(''),
  quantity_override: yup.boolean().default(false),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<AnnexFormValues>

function fromAnnex(a: AnnexOut): AnnexFormValues {
  return {
    contract_no: a.contract_no ?? '',
    annex_date: a.annex_date ?? '',
    content_before_change: a.content_before_change ?? '',
    content_after_change: a.content_after_change ?? '',
    price: a.price ?? '',
    price_override: true,
    quantity: a.quantity ?? '',
    quantity_override: true,
    note: a.note ?? '',
  }
}

function toApi(v: AnnexFormValues): AnnexCreateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  return {
    contract_no: v.contract_no.trim(),
    annex_date: v.annex_date,
    content_before_change: str(v.content_before_change),
    content_after_change: str(v.content_after_change),
    price: num(v.price),
    quantity: num(v.quantity),
    note: str(v.note),
  }
}

export function AnnexFormDialog({
  open,
  mode,
  fixedContractNo,
  initialValue,
  onClose,
  onSaved,
}: AnnexFormDialogProps) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createAnnex, { isLoading: creating }] = useCreateAnnexMutation()
  const [updateAnnex, { isLoading: updating }] = useUpdateAnnexMutation()
  const loading = creating || updating

  // Autocomplete contracts (union local_p + npe)
  const { data: contractsRes } = useListAllContractsQuery({
    page: 1,
    page_size: 500,
  })
  const contractOptions = useMemo(() => contractsRes?.items ?? [], [contractsRes])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AnnexFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  const selectedContractNo = watch('contract_no')
  const priceOverride = watch('price_override')
  const quantityOverride = watch('quantity_override')

  // Lazy load LocalP detail for prefill (works only for LocalP — NPE skip)
  const { data: contractDetailRes } = useGetLocalPQuery(selectedContractNo, {
    skip:
      !selectedContractNo ||
      // Heuristic: NPE contract_no bắt đầu với "NPE-"
      selectedContractNo.startsWith('NPE-'),
  })
  const contractDetail = contractDetailRes?.data
  const [lastFilled, setLastFilled] = useState('')

  useEffect(() => {
    if (!open) return
    setLastFilled('')
    if (isEdit && initialValue) {
      reset(fromAnnex(initialValue))
    } else {
      reset({ ...EMPTY, contract_no: fixedContractNo ?? '' })
    }
  }, [open, isEdit, initialValue, fixedContractNo, reset])

  // Auto-fill price + quantity từ contract detail (nếu chưa override)
  useEffect(() => {
    if (isEdit) return
    if (!contractDetail) return
    if (!selectedContractNo) return
    if (lastFilled === selectedContractNo) return
    if (!priceOverride) {
      setValue('price', contractDetail.outright_price ?? '')
    }
    if (!quantityOverride) {
      setValue('quantity', contractDetail.quantity_kg ?? '')
    }
    setLastFilled(selectedContractNo)
  }, [
    isEdit,
    contractDetail,
    selectedContractNo,
    lastFilled,
    priceOverride,
    quantityOverride,
    setValue,
  ])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApi(values)
      try {
        if (isEdit && initialValue) {
          const res = await updateAnnex({ id: initialValue.id, body: payload }).unwrap()
          snack.success('Đã cập nhật phụ lục')
          onSaved?.(res.data?.id ?? initialValue.id)
        } else {
          const res = await createAnnex(payload).unwrap()
          snack.success('Đã tạo phụ lục')
          onSaved?.(res.data?.id ?? 0)
        }
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(err, isEdit ? 'Cập nhật phụ lục thất bại' : 'Tạo phụ lục thất bại'),
        )
      }
    },
    () => snack.error('Vui lòng kiểm tra lại các trường còn lỗi'),
  )

  // Find supplier name for display
  const supplierDisplay = useMemo(() => {
    if (contractDetail) {
      return contractDetail.supplier_name ?? contractDetail.short_name ?? ''
    }
    const found = contractOptions.find((c) => c.contract_no === selectedContractNo)
    return found?.short_name ?? ''
  }, [contractDetail, contractOptions, selectedContractNo])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Sửa phụ lục' : 'Tạo phụ lục mới'}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {/* Row: contract autocomplete (or fixed) + amendment_no display + date */}
          <Box display="flex" gap={2} flexWrap="wrap">
            {fixedContractNo ? (
              <TextField
                label="Hợp đồng"
                size="small"
                value={fixedContractNo}
                disabled
                sx={{ flex: '2 1 280px' }}
              />
            ) : (
              <Controller
                control={control}
                name="contract_no"
                render={({ field }) => {
                  const found = contractOptions.find((c) => c.contract_no === field.value)
                  const selected =
                    found ??
                    (field.value
                      ? ({
                          contract_no: field.value,
                          short_name: null,
                          cp_code: null,
                          contract_date: null,
                          license_cert_no: null,
                          certification: null,
                          source: null,
                        } as (typeof contractOptions)[number])
                      : null)
                  return (
                    <Autocomplete
                      size="small"
                      sx={{ flex: '2 1 280px' }}
                      options={contractOptions}
                      value={selected}
                      onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                      getOptionLabel={(opt) =>
                        opt
                          ? `${opt.contract_no} — ${opt.short_name ?? opt.cp_code ?? ''}`
                          : ''
                      }
                      isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                      disabled={isEdit}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Hợp đồng *"
                          error={!!errors.contract_no}
                          helperText={errors.contract_no?.message}
                        />
                      )}
                    />
                  )
                }}
              />
            )}
            <TextField
              label="Số phụ lục"
              size="small"
              value={initialValue?.annex_no ?? '— Tự sinh —'}
              disabled
              sx={{ flex: '1 1 180px' }}
            />
            <Controller
              control={control}
              name="annex_date"
              render={({ field }) => (
                <DatePicker
                  label="Ngày phụ lục *"
                  format="DD-MMM-YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { flex: '1 1 180px' },
                      error: !!errors.annex_date,
                      helperText: errors.annex_date?.message,
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Supplier display */}
          <TextField
            label="Nhà cung cấp"
            size="small"
            value={supplierDisplay}
            disabled
            fullWidth
          />

          {/* Content before / after */}
          <Controller
            control={control}
            name="content_before_change"
            render={({ field }) => (
              <TextField
                {...field}
                label="Nội dung trước thay đổi"
                size="small"
                multiline
                minRows={3}
                fullWidth
                error={!!errors.content_before_change}
                helperText={errors.content_before_change?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="content_after_change"
            render={({ field }) => (
              <TextField
                {...field}
                label="Nội dung sau thay đổi"
                size="small"
                multiline
                minRows={3}
                fullWidth
                error={!!errors.content_after_change}
                helperText={errors.content_after_change?.message}
              />
            )}
          />

          {/* Price + override */}
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Controller
              control={control}
              name="price"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Giá"
                  type="number"
                  size="small"
                  disabled={!isEdit && !priceOverride && !!contractDetail}
                  sx={{ flex: '1 1 200px' }}
                  error={!!errors.price}
                  helperText={
                    errors.price?.message ?? 'Mặc định = outright_price của hợp đồng'
                  }
                />
              )}
            />
            {!isEdit && (
              <Controller
                control={control}
                name="price_override"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Tự nhập giá"
                  />
                )}
              />
            )}
            <Controller
              control={control}
              name="quantity"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Số lượng (kg)"
                  type="number"
                  size="small"
                  disabled={!isEdit && !quantityOverride && !!contractDetail}
                  sx={{ flex: '1 1 200px' }}
                  error={!!errors.quantity}
                  helperText={errors.quantity?.message ?? 'Mặc định = quantity_kg'}
                />
              )}
            />
            {!isEdit && (
              <Controller
                control={control}
                name="quantity_override"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Tự nhập SL"
                  />
                )}
              />
            )}
          </Box>

          <Controller
            control={control}
            name="note"
            render={({ field }) => (
              <TextField
                {...field}
                label="Ghi chú"
                size="small"
                multiline
                minRows={2}
                fullWidth
                error={!!errors.note}
                helperText={errors.note?.message}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Huỷ
        </Button>
        <Button
          onClick={() => void onSubmit()}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AnnexFormDialog
